import { prisma } from '../../shared/prisma';
import { AppError } from '../../shared/errors/AppError';

function generateMeetingCode(): string {
  const p1 = Math.random().toString(36).substring(2, 5);
  const p2 = Math.random().toString(36).substring(2, 6);
  const p3 = Math.random().toString(36).substring(2, 5);
  return `meet-${p1}-${p2}-${p3}`;
}

export async function createMeeting(data: {
  hostId: string;
  title: string;
  description?: string;
  code?: string;
  scheduledStartTime?: string | Date;
  durationMinutes?: number;
  waitingRoomEnabled?: boolean;
  allowChat?: boolean;
  allowScreenShare?: boolean;
  allowMic?: boolean;
  allowCamera?: boolean;
  transcriptEnabled?: boolean;
  invitedUserIds?: string[];
  invitedDepartmentIds?: string[];
  settings?: any;
}) {
  const code = data.code?.trim() || generateMeetingCode();
  const startTime = data.scheduledStartTime ? new Date(data.scheduledStartTime) : new Date();
  const duration = data.durationMinutes || 30;
  const endTime = new Date(startTime.getTime() + duration * 60 * 1000);

  const meeting = await prisma.meeting.create({
    data: {
      code,
      title: data.title || 'Quick Instant Meeting',
      description: data.description || null,
      hostId: data.hostId,
      scheduledStartTime: startTime,
      scheduledEndTime: endTime,
      durationMinutes: duration,
      status: 'scheduled',
      waitingRoomEnabled: !!data.waitingRoomEnabled,
      allowChat: data.allowChat !== false,
      allowScreenShare: data.allowScreenShare !== false,
      allowMic: data.allowMic !== false,
      allowCamera: data.allowCamera !== false,
      transcriptEnabled: data.transcriptEnabled !== false,
      invitedUserIds: data.invitedUserIds || [],
      invitedDepartmentIds: data.invitedDepartmentIds || [],
      settings: data.settings || {},
      participants: {
        create: {
          userId: data.hostId,
          role: 'host',
          status: 'admitted',
        },
      },
    },
    include: {
      host: {
        select: { id: true, displayName: true, avatarUrl: true, email: true, department: true },
      },
      participants: {
        include: {
          user: { select: { id: true, displayName: true, avatarUrl: true, email: true } },
        },
      },
    },
  });

  return meeting;
}

export async function listUserMeetings(userId: string, query: {
  startDate?: string;
  endDate?: string;
  filter?: 'upcoming' | 'past' | 'all';
  search?: string;
}) {
  const now = new Date();
  const user = await prisma.user.findUnique({
    where: { id: userId },
    select: { id: true, departmentId: true },
  });

  const departmentId = user?.departmentId;

  const whereClause: any = {
    OR: [
      { hostId: userId },
      { participants: { some: { userId } } },
      { invitedUserIds: { array_contains: [userId] } },
    ],
  };

  if (departmentId) {
    whereClause.OR.push({ invitedDepartmentIds: { array_contains: [departmentId] } });
  }

  // Date Range filter for Calendar View
  if (query.startDate && query.endDate) {
    whereClause.scheduledStartTime = {
      gte: new Date(query.startDate),
      lte: new Date(query.endDate),
    };
  } else if (query.filter === 'upcoming') {
    whereClause.OR = whereClause.OR.map((cond: any) => ({
      ...cond,
      scheduledStartTime: { gte: new Date(now.getTime() - 2 * 60 * 60 * 1000) },
      status: { in: ['scheduled', 'live'] },
    }));
  } else if (query.filter === 'past') {
    whereClause.OR = whereClause.OR.map((cond: any) => ({
      ...cond,
      status: { in: ['ended', 'cancelled'] },
    }));
  }

  if (query.search?.trim()) {
    whereClause.AND = [
      {
        OR: [
          { title: { contains: query.search.trim(), mode: 'insensitive' } },
          { code: { contains: query.search.trim(), mode: 'insensitive' } },
          { description: { contains: query.search.trim(), mode: 'insensitive' } },
        ],
      },
    ];
  }

  const meetings = await prisma.meeting.findMany({
    where: whereClause,
    include: {
      host: {
        select: { id: true, displayName: true, avatarUrl: true, email: true, department: true },
      },
      participants: {
        include: {
          user: { select: { id: true, displayName: true, avatarUrl: true, email: true } },
        },
      },
    },
    orderBy: { scheduledStartTime: 'asc' },
  });

  return meetings;
}

export async function getMeetingByCodeOrId(codeOrId: string) {
  const meeting = await prisma.meeting.findFirst({
    where: {
      OR: [{ id: codeOrId }, { code: codeOrId.toLowerCase().trim() }],
    },
    include: {
      host: {
        select: { id: true, displayName: true, avatarUrl: true, email: true, department: true },
      },
      participants: {
        include: {
          user: { select: { id: true, displayName: true, avatarUrl: true, email: true } },
        },
      },
    },
  });

  if (!meeting) {
    throw new AppError(404, 'Meeting not found');
  }

  return meeting;
}

export async function updateMeeting(meetingId: string, userId: string, updateData: {
  title?: string;
  description?: string;
  scheduledStartTime?: string | Date;
  durationMinutes?: number;
  status?: string;
  isLocked?: boolean;
  waitingRoomEnabled?: boolean;
  allowChat?: boolean;
  allowScreenShare?: boolean;
  allowMic?: boolean;
  allowCamera?: boolean;
  transcriptEnabled?: boolean;
  invitedUserIds?: string[];
  invitedDepartmentIds?: string[];
  settings?: any;
}) {
  const existing = await prisma.meeting.findUnique({ where: { id: meetingId } });
  if (!existing) throw new AppError(404, 'Meeting not found');
  if (existing.hostId !== userId) {
    throw new AppError(403, 'Only the meeting host can modify settings');
  }

  const data: any = { ...updateData };
  if (updateData.scheduledStartTime && updateData.durationMinutes) {
    const start = new Date(updateData.scheduledStartTime);
    data.scheduledStartTime = start;
    data.scheduledEndTime = new Date(start.getTime() + updateData.durationMinutes * 60 * 1000);
  }

  if (updateData.status === 'live' && !existing.actualStartTime) {
    data.actualStartTime = new Date();
  } else if (updateData.status === 'ended' && !existing.actualEndTime) {
    data.actualEndTime = new Date();
  }

  return prisma.meeting.update({
    where: { id: meetingId },
    data,
    include: {
      host: {
        select: { id: true, displayName: true, avatarUrl: true, email: true, department: true },
      },
      participants: {
        include: {
          user: { select: { id: true, displayName: true, avatarUrl: true, email: true } },
        },
      },
    },
  });
}

export async function registerParticipantJoin(meetingId: string, participantData: {
  userId?: string;
  guestName?: string;
  role?: string;
  status?: string;
}) {
  const meeting = await prisma.meeting.findUnique({ where: { id: meetingId } });
  if (!meeting) throw new AppError(404, 'Meeting not found');

  let participant = null;
  if (participantData.userId) {
    participant = await prisma.meetingParticipant.findFirst({
      where: { meetingId, userId: participantData.userId },
    });
  }

  const isHost = meeting.hostId === participantData.userId;
  const resolvedRole = isHost ? 'host' : (participantData.role || (participantData.userId ? 'participant' : 'guest'));
  const resolvedStatus = isHost || !meeting.waitingRoomEnabled ? 'admitted' : (participantData.status || 'waiting');

  if (participant) {
    return prisma.meetingParticipant.update({
      where: { id: participant.id },
      data: {
        role: resolvedRole,
        status: resolvedStatus,
        leftAt: null,
      },
      include: {
        user: { select: { id: true, displayName: true, avatarUrl: true, email: true } },
      },
    });
  }

  return prisma.meetingParticipant.create({
    data: {
      meetingId,
      userId: participantData.userId || null,
      guestName: participantData.guestName || (isHost ? 'Host' : 'Guest'),
      role: resolvedRole,
      status: resolvedStatus,
    },
    include: {
      user: { select: { id: true, displayName: true, avatarUrl: true, email: true } },
    },
  });
}

export async function registerParticipantLeave(meetingId: string, participantIdOrUserId: string) {
  const participant = await prisma.meetingParticipant.findFirst({
    where: {
      meetingId,
      OR: [{ id: participantIdOrUserId }, { userId: participantIdOrUserId }],
    },
  });

  if (!participant) return null;

  const now = new Date();
  const durationSec = Math.max(0, Math.floor((now.getTime() - new Date(participant.joinedAt).getTime()) / 1000));

  return prisma.meetingParticipant.update({
    where: { id: participant.id },
    data: {
      leftAt: now,
      status: 'left',
      duration: durationSec,
    },
  });
}

export async function appendTranscript(meetingId: string, segment: {
  id: string;
  speakerId?: string;
  speakerName: string;
  text: string;
  timestamp: number;
}) {
  const meeting = await prisma.meeting.findUnique({ where: { id: meetingId }, select: { transcript: true } });
  if (!meeting) throw new AppError(404, 'Meeting not found');

  const existingTranscript = Array.isArray(meeting.transcript) ? meeting.transcript : [];
  const updatedTranscript = [...existingTranscript, segment];

  return prisma.meeting.update({
    where: { id: meetingId },
    data: { transcript: updatedTranscript },
  });
}

export async function getMeetingReport(meetingId: string) {
  const meeting = await prisma.meeting.findUnique({
    where: { id: meetingId },
    include: {
      host: {
        select: { id: true, displayName: true, avatarUrl: true, email: true, department: true },
      },
      participants: {
        include: {
          user: { select: { id: true, displayName: true, avatarUrl: true, email: true } },
        },
        orderBy: { joinedAt: 'asc' },
      },
    },
  });

  if (!meeting) throw new AppError(404, 'Meeting not found');

  const totalAttendees = meeting.participants.length;
  const transcriptSegments = Array.isArray(meeting.transcript) ? meeting.transcript : [];

  return {
    meeting: {
      id: meeting.id,
      code: meeting.code,
      title: meeting.title,
      description: meeting.description,
      hostName: meeting.host.displayName,
      hostEmail: meeting.host.email,
      scheduledStartTime: meeting.scheduledStartTime,
      scheduledEndTime: meeting.scheduledEndTime,
      actualStartTime: meeting.actualStartTime,
      actualEndTime: meeting.actualEndTime,
      status: meeting.status,
      durationMinutes: meeting.durationMinutes,
      totalAttendees,
    },
    attendance: meeting.participants.map((p) => ({
      id: p.id,
      name: p.user?.displayName || p.guestName || 'Guest',
      email: p.user?.email || 'N/A',
      role: p.role,
      joinedAt: p.joinedAt,
      leftAt: p.leftAt,
      durationSeconds: p.duration,
      status: p.status,
    })),
    transcript: transcriptSegments,
  };
}

export async function deleteMeeting(meetingId: string, userId: string) {
  const existing = await prisma.meeting.findUnique({ where: { id: meetingId } });
  if (!existing) throw new AppError(404, 'Meeting not found');
  if (existing.hostId !== userId) {
    throw new AppError(403, 'Only the meeting host can delete this meeting');
  }

  return prisma.meeting.delete({ where: { id: meetingId } });
}
